import { useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Spinner,
  useModal,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { postGatedFeatureInteraction } from './api';

type CtaModalData = {
  /** Records the interaction; bound in the wrapper (which has provider access). */
  onRecord: () => Promise<void>;
};

/**
 * Confirmation modal shown after the user asks to upgrade. Managed modals mount
 * *outside* the app's provider tree, so this stays presentational: it takes the
 * already-bound `onRecord` callback as data and tracks only local UI state
 * rather than calling data-layer/context hooks itself. Port of the Ember
 * gated-features CTA modal.
 */
function GatedFeaturesCtaModal({ modalState, data, onClose }: PlumaManagedModalProps<CtaModalData>) {
  const [isRecording, setIsRecording] = useState(true);

  const { onRecord } = data;
  useEffect(() => {
    // Fire once when the modal opens, mirroring the Ember `sendEvent` task. On
    // failure the wrapper surfaces the error toast and we just close.
    onRecord()
      .then(() => setIsRecording(false))
      .catch(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run exactly once on open
  }, []);

  return (
    <Modal state={modalState} onClose={onClose} size="sm" title="We'll be in touch soon!">
      {isRecording ? (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      ) : (
        <>
          <ModalBody>
            <Paragraph>
              We've let our sales team know you're interested in upgrading to Premium. Keep an eye on your inbox for an
              email from us with next steps.
            </Paragraph>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="center">
              <Button data-test-gated-features-cta-close-button variant="primary" onClick={onClose}>
                Continue exploring
              </Button>
            </Flex>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}

type GatedFeaturesCallToActionProps = {
  featureName?: string;
  children: (api: { trigger: () => void }) => ReactNode;
};

/**
 * Renders an upgrade trigger (via render prop) that opens the gated-features
 * confirmation modal. Fires the Segment analytics event and binds the
 * `gated_feature_interaction` recording here (inside the provider tree) since
 * the managed modal itself can't reach those contexts. Port of the Ember
 * `gated-features/call-to-action` component.
 */
export function GatedFeaturesCallToAction({ featureName = '', children }: GatedFeaturesCallToActionProps) {
  const { account, user } = useAuth();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const ctaModal = useModal(GatedFeaturesCtaModal);

  const requester = `${user.first_name} ${user.last_name}`;

  const trigger = () => {
    void track('plan_billing', 'switchToPremiumButtonClicked', {
      'Account Id': account.id,
      'Feature Name': featureName,
      Requester: requester,
      'Requester Email': user.email,
      'Requester User Id': user.id,
    });

    ctaModal.open({
      onRecord: async () => {
        try {
          await postGatedFeatureInteraction({
            accountId: account.id,
            featureName,
            requester,
            requesterEmail: user.email,
            requesterUserId: user.id,
          });
        } catch (error) {
          snackbars.create({
            id: 'gated-feature-cta-error',
            variant: 'critical',
            message: 'Something went wrong letting our team know. Please try again.',
          });
          throw error;
        }
      },
    });
  };

  return <>{children({ trigger })}</>;
}
